// Clip library page with filtering and horizontal batching.

import React, { useCallback, useEffect, useMemo } from 'react';
import { ClapperboardIcon } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  DEFAULT_CLIP_BATCH_SIZE,
  planClipOrder,
  Rail,
} from '../../components/clips';
import { Button } from '../../components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../../components/ui/empty';
import { Separator } from '../../components/ui/separator';
import SEO from '../../seo/SEO';
import { getAllClips } from '@airhead-games/shared/utilities';
import {
  findGameDiscoverySlug,
  findGameIdByDiscoverySlug,
  findGameName,
} from '@airhead-games/shared/utilities/getGames';
import { analyticsService } from '../../services/analyticsService';
import {
  CollectionToolbar,
  FilterControls,
} from '../../components/collectionToolbar';
import { getGradientSurfaceStyle } from '@/styles/gradientSurfaceStyle';
import { useRouteQueryState } from '../../hooks';

const Clips: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const allClips = useMemo(() => getAllClips(), []);

  const uniqueStreamers = useMemo(() => {
    const streamers = [...new Set(allClips.map((clip) => clip.streamerName))];
    return streamers.sort((a, b) => a.localeCompare(b));
  }, [allClips]);

  const uniqueGameIds = useMemo(() => {
    const gameIds = [...new Set(allClips.map((clip) => clip.gameId))];
    return gameIds.sort((a, b) => {
      const gameNameA = findGameName(a) || a;
      const gameNameB = findGameName(b) || b;
      return gameNameA.localeCompare(gameNameB);
    });
  }, [allClips]);

  const gameSet = useMemo(() => new Set(uniqueGameIds), [uniqueGameIds]);
  const streamerSet = useMemo(
    () => new Set(uniqueStreamers),
    [uniqueStreamers]
  );

  const { selected, updateRoute } = useRouteQueryState<{
    games: string[];
    streamers: string[];
  }>({
    pathname: '/discover/clips',
    searchParams,
    navigate,
    facets: {
      games: {
        key: 'game',
        parse: (raw) => {
          const gameId = findGameIdByDiscoverySlug(raw);
          return gameId && gameSet.has(gameId) ? gameId : undefined;
        },
        serialize: (gameId) => findGameDiscoverySlug(gameId) || gameId,
      },
      streamers: {
        key: 'streamer',
        parse: (raw) => (streamerSet.has(raw) ? raw : undefined),
      },
    },
  });

  const selectedGameIds = selected.games;
  const selectedStreamers = selected.streamers;

  const availableGameIds = useMemo(() => {
    if (selectedStreamers.length === 0) {
      return uniqueGameIds;
    }

    const allowedGames = new Set(
      allClips
        .filter((clip) => selectedStreamers.includes(clip.streamerName))
        .map((clip) => clip.gameId)
    );

    return uniqueGameIds.filter((gameId) => allowedGames.has(gameId));
  }, [allClips, selectedStreamers, uniqueGameIds]);

  const availableStreamers = useMemo(() => {
    if (selectedGameIds.length === 0) {
      return uniqueStreamers;
    }

    const allowedStreamers = new Set(
      allClips
        .filter((clip) => selectedGameIds.includes(clip.gameId))
        .map((clip) => clip.streamerName)
    );

    return uniqueStreamers.filter((streamer) => allowedStreamers.has(streamer));
  }, [allClips, selectedGameIds, uniqueStreamers]);

  const activeGameIds = useMemo(
    () => selectedGameIds.filter((gameId) => availableGameIds.includes(gameId)),
    [selectedGameIds, availableGameIds]
  );

  const activeStreamers = useMemo(
    () =>
      selectedStreamers.filter((streamer) =>
        availableStreamers.includes(streamer)
      ),
    [selectedStreamers, availableStreamers]
  );

  const orderedGameIds = useMemo(() => {
    const selectedSet = new Set(activeGameIds);
    return [
      ...activeGameIds,
      ...availableGameIds.filter((gameId) => !selectedSet.has(gameId)),
    ];
  }, [activeGameIds, availableGameIds]);

  const orderedStreamers = useMemo(() => {
    const selectedSet = new Set(activeStreamers);
    return [
      ...activeStreamers,
      ...availableStreamers.filter((streamer) => !selectedSet.has(streamer)),
    ];
  }, [activeStreamers, availableStreamers]);

  useEffect(() => {
    if (
      activeGameIds.length === selectedGameIds.length &&
      activeStreamers.length === selectedStreamers.length
    ) {
      return;
    }

    updateRoute(
      {
        games: activeGameIds,
        streamers: activeStreamers,
      },
      { replace: true }
    );
  }, [
    activeGameIds,
    activeStreamers,
    selectedGameIds,
    selectedStreamers,
    updateRoute,
  ]);

  const clips = useMemo(
    () =>
      planClipOrder(allClips, {
        gameIds: activeGameIds,
        streamerNames: activeStreamers,
        batchSize: DEFAULT_CLIP_BATCH_SIZE,
      }),
    [allClips, activeGameIds, activeStreamers]
  );

  const hasLibraryClips = allClips.length > 0;
  const hasFilteredClips = clips.length > 0;

  const updateFilters = useCallback(
    (gameIds: string[], streamers: string[]) => {
      updateRoute({
        games: gameIds,
        streamers,
      });
    },
    [updateRoute]
  );

  const trackFilterChange = useCallback(
    (
      filterType: 'game' | 'streamer',
      value: string,
      action: 'add' | 'remove'
    ) => {
      analyticsService.trackButtonClick(`Clips Filter ${filterType}`, {
        filter_value: value,
        filter_action: action,
        link_location: 'clips_filter_panel',
      });
    },
    []
  );

  const toggleGame = (gameId: string) => {
    const isSelected = activeGameIds.includes(gameId);
    const gameName = findGameName(gameId) || gameId;
    trackFilterChange('game', gameName, isSelected ? 'remove' : 'add');
    const nextGames = isSelected
      ? activeGameIds.filter((id) => id !== gameId)
      : [...activeGameIds, gameId];
    updateFilters(nextGames, activeStreamers);
  };

  const toggleStreamer = (streamer: string) => {
    const isSelected = activeStreamers.includes(streamer);
    trackFilterChange('streamer', streamer, isSelected ? 'remove' : 'add');
    const nextStreamers = isSelected
      ? activeStreamers.filter((name) => name !== streamer)
      : [...activeStreamers, streamer];
    updateFilters(activeGameIds, nextStreamers);
  };

  const handleLoadMore = useCallback(
    (nextVisibleCount: number, totalCount: number) => {
      analyticsService.trackButtonClick('Load More Clips', {
        current_count: Math.max(
          nextVisibleCount - DEFAULT_CLIP_BATCH_SIZE,
          DEFAULT_CLIP_BATCH_SIZE
        ),
        next_count: nextVisibleCount,
        total_count: totalCount,
        link_location: 'clips_gallery',
      });
    },
    []
  );

  const activeFilterItems = useMemo(
    () => [
      ...activeGameIds.map((gameId) => {
        const gameName = findGameName(gameId) || gameId;
        return {
          id: `game:${gameId}`,
          label: gameName,
          onRemove: () => {
            trackFilterChange('game', gameName, 'remove');
            updateFilters(
              activeGameIds.filter((id) => id !== gameId),
              activeStreamers
            );
          },
        };
      }),
      ...activeStreamers.map((streamer) => ({
        id: `streamer:${streamer}`,
        label: streamer,
        onRemove: () => {
          trackFilterChange('streamer', streamer, 'remove');
          updateFilters(
            activeGameIds,
            activeStreamers.filter((name) => name !== streamer)
          );
        },
      })),
    ],
    [activeGameIds, activeStreamers, updateFilters, trackFilterChange]
  );
  const hasActiveFilters = activeFilterItems.length > 0;
  const videoCountLabel = `${clips.length} video${clips.length === 1 ? '' : 's'}`;

  return (
    <div className="min-h-svh bg-background pt-20 text-foreground sm:pt-24 md:pt-28 lg:pt-32">
      <SEO />
      <main className="mx-auto flex w-full max-w-screen-2xl flex-col gap-3 px-4 pb-16 sm:gap-4 sm:px-6 md:px-8 lg:gap-8 lg:px-10 xl:px-12 2xl:px-16">
        <header className="flex flex-col gap-8">
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-muted-foreground"
          >
            <ol className="flex flex-wrap items-center gap-2">
              <li className="flex items-center gap-2">
                <Link to="/discover" className="hover:text-primary">
                  Discover
                </Link>
              </li>
              <li className="flex items-center gap-2">
                <span aria-hidden="true">/</span>
                <span className="text-foreground">Clips</span>
              </li>
            </ol>
          </nav>

          <div
            className="relative overflow-hidden rounded-lg ring-1 ring-white/20"
            style={getGradientSurfaceStyle('yellow')}
          >
            <ClapperboardIcon
              aria-hidden="true"
              strokeWidth={1.75}
              className="pointer-events-none absolute top-5 right-5 z-10 size-9 text-white sm:top-6 sm:right-6 sm:size-10 md:top-8 md:right-8 md:size-12 lg:top-10 lg:right-10 lg:size-14"
            />
            <div className="relative grid gap-6 md:gap-8">
              <div className="flex max-w-5xl flex-col gap-4 p-5 pr-16 sm:p-6 sm:pr-20 md:p-8 md:pr-24 lg:p-10 lg:pr-28">
                <h1 className="font-heading text-4xl leading-tight font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-6xl 2xl:text-7xl">
                  Clips
                </h1>
                <p className="max-w-4xl text-base leading-6 text-white/80 sm:text-lg sm:leading-7 xl:text-xl xl:leading-8">
                  Funny reveals and other highlight moments from the Airhead
                  Games community.
                </p>
              </div>
            </div>
          </div>

          <Separator />
        </header>

        <CollectionToolbar
          filters={
            <FilterControls
              groups={[
                {
                  id: 'games',
                  label: 'Games',
                  options: orderedGameIds.map((gameId) => ({
                    value: gameId,
                    label: findGameName(gameId) || gameId,
                  })),
                  selectedValues: activeGameIds,
                  onToggle: toggleGame,
                  searchPlaceholder: 'Search games',
                },
                {
                  id: 'streamers',
                  label: 'Streamers',
                  options: orderedStreamers.map((streamer) => ({
                    value: streamer,
                    label: streamer,
                  })),
                  selectedValues: activeStreamers,
                  onToggle: toggleStreamer,
                  searchPlaceholder: 'Search streamers',
                },
              ]}
              activeItems={activeFilterItems}
              onClearAll={() => updateFilters([], [])}
            />
          }
        />

        <section className="flex min-w-0 flex-col gap-3">
          <div className="flex items-baseline justify-between gap-4 px-2">
            <div className="flex min-w-0 items-baseline gap-3 max-[480px]:gap-2">
              <h2 className="font-heading text-xl leading-tight font-semibold tracking-tight md:text-2xl">
                Gameplay Clips
              </h2>
              <span className="text-sm leading-6 text-muted-foreground max-[480px]:text-xs">
                {videoCountLabel}
              </span>
            </div>
          </div>

          {!hasLibraryClips ? (
            <Empty className="border bg-muted/10 py-16">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ClapperboardIcon aria-hidden="true" />
                </EmptyMedia>
                <EmptyTitle>Clips are unavailable</EmptyTitle>
                <EmptyDescription>
                  The clip library did not load for this page render.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : !hasFilteredClips ? (
            <Empty className="border bg-muted/10 py-16">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ClapperboardIcon aria-hidden="true" />
                </EmptyMedia>
                <EmptyTitle>No clips match these filters</EmptyTitle>
                <EmptyDescription>
                  Try removing a game or streamer filter to widen the reel.
                </EmptyDescription>
              </EmptyHeader>
              {hasActiveFilters ? (
                <EmptyContent>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateFilters([], [])}
                  >
                    Clear all filters
                  </Button>
                </EmptyContent>
              ) : null}
            </Empty>
          ) : (
            <Rail
              clips={clips}
              showGameChip={false}
              showChips={false}
              width={560}
              height={315}
              initialEagerCount={DEFAULT_CLIP_BATCH_SIZE}
              batchSize={DEFAULT_CLIP_BATCH_SIZE}
              onLoadMore={handleLoadMore}
            />
          )}
        </section>
      </main>
    </div>
  );
};

export default Clips;
